/*
 * aiClerk 调试设置 — 仅 dev/test/staging 通过 SettingsPopover 让用户切换
 *
 * 状态:
 *   showToolDetails — 展示完整 tool call 列表 (默认 false; 默认走极简 "正在读取文档" 文案)
 *   showReasoning   — 已下线: 恒 false, 思考内容不再展示 (字段保留供消费方读取)
 *
 * 持久化: localStorage. 写在 reducer 里是 side effect, 但跟当前仓库其他 slice 风格简单一致;
 * 不引入 redux-persist (避免 store.ts 改造面扩散).
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

const STORAGE_KEY = 'aiClerk:debugSettings:v1'

export interface AiClerkSettingsState {
  showToolDetails: boolean
  showReasoning: boolean
}

const DEFAULTS: AiClerkSettingsState = {
  showToolDetails: false,
  showReasoning: false
}

function loadFromStorage(): AiClerkSettingsState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULTS
    const parsed = JSON.parse(raw) as Partial<AiClerkSettingsState>
    return {
      showToolDetails:
        typeof parsed.showToolDetails === 'boolean'
          ? parsed.showToolDetails
          : DEFAULTS.showToolDetails,
      // 思考内容展示已下线: 恒 false (忽略历史 localStorage 里可能存的 true)
      showReasoning: false
    }
  } catch {
    return DEFAULTS
  }
}

function saveToStorage(state: AiClerkSettingsState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* no-op */
  }
}

const aiClerkSettingsSlice = createSlice({
  name: 'aiClerkSettings',
  initialState: loadFromStorage(),
  reducers: {
    setShowToolDetails: (state, action: PayloadAction<boolean>) => {
      state.showToolDetails = action.payload
      saveToStorage(state)
    }
  }
})

export const { setShowToolDetails } = aiClerkSettingsSlice.actions

export default aiClerkSettingsSlice.reducer
